// src/pages/schooladmin/assingments.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup,
  Form, InputGroup, Modal, FloatingLabel, Badge, Spinner
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy, where
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, hh:mm A") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";

/* ---------------- form defaults ---------------- */
const DEFAULT = {
  title: "",
  subject: "",
  className: "",
  section: "",
  branch: "",           // NEW
  description: "",
  dueAt: "",
  attachURL: "",      // optional external link (Drive, etc.)
  maxMarks: "",
  status: "active",   // active | inactive
  teacherId: "",      // optional: who assigned
  teacherName: "",    // stored denormalized
};

/* ---------------- modal ---------------- */
function AssignmentFormModal({
  show,
  onHide,
  initial,
  onSave,
  classes = [],
  teachers = [],
  subjects = [], // optional collection: {name, className?, branch?}
}) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  // ---- Fallbacks when Firestore classes are empty ----
  const CLASS_FALLBACK = useMemo(() => ([
    ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),
    "Foundation 6","Foundation 7","Foundation 8","Foundation 9","Foundation 10",
    "NEET","JEE","Class 11 NEET","Class 11 JEE","Class 12 NEET","Class 12 JEE",
    "Integrated","LT",
  ]), []);
  const SECTION_FALLBACK = useMemo(() => (["A","B","C","D","E"]), []);

  const BRANCH_FALLBACK = useMemo(() => ([
    "Science","Commerce","Arts",
    "PCM","PCB","PCMB",
    "CSE","IT","ECE","EEE","Mechanical","Civil",
    "AI/ML","Data Science","Biotech","Chemical","Mechatronics","Automobile",
    "General","Finance","Marketing","HR","Business Analytics","Logistics","Computer Applications"
  ]), []);

  const branchOptions = useMemo(() => {
    // Try to gather branches hinted by subjects (optional) or fallback
    const fromSubjects = (subjects || [])
      .map(s => (s.branch || s.stream || "").toString().trim())
      .filter(Boolean);
    const uniq = Array.from(new Set(fromSubjects)).sort();
    return uniq.length ? uniq : BRANCH_FALLBACK;
  }, [subjects, BRANCH_FALLBACK]);

  useEffect(() => {
    if (initial) {
      setForm({
        title: initial.title || "",
        subject: initial.subject || "",
        className: initial.className || "",
        section: initial.section || "",
        branch: initial.branch || "",           // NEW
        description: initial.description || "",
        dueAt: initial.dueAt ? dayjs(toDate(initial.dueAt)).format("YYYY-MM-DDTHH:mm") : "",
        attachURL: initial.attachURL || "",
        maxMarks: initial.maxMarks || "",
        status: initial.status || "active",
        teacherId: initial.teacherId || "",
        teacherName: initial.teacherName || "",
      });
    } else {
      setForm({ ...DEFAULT });
    }
  }, [initial, show]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Class/Section options
  const { classNames, usingFallback } = useMemo(() => {
    const fromFs = (classes || []).map(c => (c.className || c.name || "").trim()).filter(Boolean);
    const uniqFs = Array.from(new Set(fromFs)).sort();
    return uniqFs.length ? { classNames: uniqFs, usingFallback: false }
                         : { classNames: CLASS_FALLBACK, usingFallback: true };
  }, [classes, CLASS_FALLBACK]);

  const sectionOptions = useMemo(() => {
    if (!form.className) return [];
    if (!usingFallback) {
      const secs = (classes || [])
        .filter(c => (c.className || c.name) === form.className)
        .map(c => (c.section || "").trim())
        .filter(Boolean);
      return Array.from(new Set(secs)).sort();
    }
    return SECTION_FALLBACK;
  }, [classes, form.className, usingFallback, SECTION_FALLBACK]);

  // Teachers dropdown (optional)
  const teacherOptions = useMemo(
    () => (teachers || [])
      .map(t => ({ id: t.id, name: t.name || "", email: t.email || "", primarySubject: t.primarySubject || "" }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [teachers]
  );

  // Subjects datalist (filtered by className if present in doc)
  const subjectOptions = useMemo(() => {
    const list = (subjects || [])
      .filter(s => {
        if (!s?.name) return false;
        if (s.className && form.className) return s.className === form.className;
        return true;
      })
      .map(s => String(s.name).trim())
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [subjects, form.className]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return Swal.fire({ icon: "warning", title: "Title is required" });
    if (!form.className.trim()) return Swal.fire({ icon: "warning", title: "Class is required" });
    if (!form.dueAt) return Swal.fire({ icon: "warning", title: "Due date/time is required" });

    if (form.attachURL && !/^https?:\/\//i.test(form.attachURL.trim())) {
      return Swal.fire({ icon: "info", title: "Attachment link must start with http:// or https://" });
    }

    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        subject: form.subject.trim(),
        className: form.className.trim(),
        section: form.section.trim(),
        branch: form.branch.trim(),                     // NEW
        description: form.description.trim(),
        dueAt: form.dueAt ? new Date(form.dueAt) : null,
        attachURL: form.attachURL.trim(),
        maxMarks: form.maxMarks ? Number(form.maxMarks) : null,
        status: form.status || "active",
        teacherId: form.teacherId || "",
        teacherName: form.teacherName.trim(),
      });
      onHide?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{initial ? "Edit Assignment" : "Create Assignment"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={8}>
              <FloatingLabel label="Title *">
                <Form.Control
                  value={form.title}
                  onChange={(e) => change("title", e.target.value)}
                  placeholder="Trigonometry Homework Set 1"
                  required
                />
              </FloatingLabel>
            </Col>
            <Col md={4}>
              <FloatingLabel label="Subject">
                <Form.Control
                  list="subjects-list"
                  value={form.subject}
                  onChange={(e) => change("subject", e.target.value)}
                  placeholder="Mathematics"
                />
                <datalist id="subjects-list">
                  {subjectOptions.map((s) => <option key={s} value={s} />)}
                </datalist>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Class *">
                <Form.Select
                  value={form.className}
                  onChange={(e) => change("className", e.target.value) || change("section", "")}
                  required
                >
                  <option value="">— Select class —</option>
                  {classNames.map(c => <option key={c} value={c}>{c}</option>)}
                  {form.className && !classNames.includes(form.className) && (
                    <option value={form.className}>{form.className} (custom)</option>
                  )}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Section">
                <Form.Select
                  value={form.section}
                  onChange={(e) => change("section", e.target.value)}
                  disabled={!sectionOptions.length}
                >
                  <option value="">{sectionOptions.length ? "— Select section —" : "— none —"}</option>
                  {sectionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Branch">
                <Form.Select
                  value={form.branch}
                  onChange={(e) => change("branch", e.target.value)}
                  disabled={!branchOptions.length}
                >
                  <option value="">{branchOptions.length ? "— Select branch —" : "— none —"}</option>
                  {branchOptions.map(b => <option key={b} value={b}>{b}</option>)}
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Status">
                <Form.Select value={form.status} onChange={(e) => change("status", e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </FloatingLabel>
            </Col>

            <Col md={3}>
              <FloatingLabel label="Max Marks">
                <Form.Control
                  type="number"
                  min="0"
                  value={form.maxMarks}
                  onChange={(e) => change("maxMarks", e.target.value)}
                  placeholder="100"
                />
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Due at *">
                <Form.Control
                  type="datetime-local"
                  value={form.dueAt}
                  onChange={(e) => change("dueAt", e.target.value)}
                  required
                />
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Attachment Link (Drive/Docs/URL)">
                <Form.Control
                  type="url"
                  value={form.attachURL}
                  onChange={(e) => change("attachURL", e.target.value)}
                  placeholder="https://drive.google.com/..."
                />
              </FloatingLabel>
            </Col>

            {/* Optional: who assigns */}
            <Col md={6}>
              <FloatingLabel label="Assigned by (teacher)">
                <Form.Select
                  value={form.teacherId}
                  onChange={(e) => {
                    const t = teacherOptions.find(x => x.id === e.target.value);
                    change("teacherId", e.target.value);
                    change("teacherName", t?.name || "");
                    if (t?.primarySubject) change("subject", t.primarySubject);
                  }}
                >
                  <option value="">— Select teacher —</option>
                  {teacherOptions.map(t => (
                    <option key={t.id} value={t.id}>{t.name}{t.email ? ` (${t.email})` : ""}</option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              <div className="form-text">Or leave blank and type a custom name.</div>
            </Col>

            <Col md={6}>
              <FloatingLabel label="Custom teacher name (optional)">
                <Form.Control
                  value={form.teacherName}
                  onChange={(e) => change("teacherName", e.target.value)}
                  placeholder="Mr. Rao"
                />
              </FloatingLabel>
            </Col>

            <Col md={12}>
              <FloatingLabel label="Description / Instructions">
                <Form.Control
                  as="textarea"
                  style={{ height: 120 }}
                  value={form.description}
                  onChange={(e) => change("description", e.target.value)}
                  placeholder="Problem set details, submission instructions, etc."
                />
              </FloatingLabel>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* ---------------- main page ---------------- */
export default function AssignmentsPage() {
  const { school, loading: loadingSchool } = useSchool();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // user & role
  const [user, setUser] = useState(null);          // auth user
  const [role, setRole] = useState(null);          // "schooladmin" | "teacher" | null

  // referenced data
  const [classesData, setClassesData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);
  const [subjectsData, setSubjectsData] = useState([]);

  // filters
  const [qText, setQText] = useState("");
  const [cls, setCls] = useState("all");
  const [sec, setSec] = useState("all");
  const [branch, setBranch] = useState("all");                 // NEW (moved before filtered useMemo)
  const [subj, setSubj] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const searchRef = useRef(null);

  // listen auth + role
  useEffect(() => {
    const auth = getAuth();
    const off = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      // fetch role from /users/{uid}
      if (u?.uid) {
        const unsubUser = onSnapshot(doc(db, "users", u.uid), (snap) => {
          const data = snap.data() || {};
          setRole(data.role || null);
        });
        return () => unsubUser();
      } else {
        setRole(null);
      }
    });
    return () => off();
  }, []);

  // live assignments (role-aware)
  useEffect(() => {
    if (!school?.id) return;
    if (role == null) return; // wait until role known

    setLoading(true);
    const colRef = collection(db, "schools", school.id, "assignments");

    // Admin -> all; Teacher -> only createdBy == me
    const qRef = role === "teacher" && user?.uid
      ? query(colRef, where("createdBy", "==", user.uid), orderBy("dueAt", "desc"))
      : query(colRef, orderBy("dueAt", "desc"));

    const unsub = onSnapshot(qRef, (snap) => {
      const list = snap.docs.map((d) => {
        const x = d.data() || {};
        return {
          id: d.id,
          title: x.title || "—",
          subject: x.subject || "",
          className: x.className || "",
          section: x.section || "",
          branch: x.branch || "",                 // NEW
          description: x.description || "",
          dueAt: toDate(x.dueAt),
          attachURL: x.attachURL || "",
          maxMarks: x.maxMarks ?? null,
          status: x.status || "active",
          teacherId: x.teacherId || "",
          teacherName: x.teacherName || "",
          createdBy: x.createdBy || "",
          createdByRole: x.createdByRole || "",
          createdAt: toDate(x.createdAt),
          updatedAt: toDate(x.updatedAt),
        };
      });
      setRows(list);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [school?.id, role, user?.uid]);

  // subscribe classes, teachers, subjects (school-scoped)
  useEffect(() => {
    if (!school?.id) return;

    // classes
    const colClasses = collection(db, "schools", school.id, "classes");
    const unC = onSnapshot(query(colClasses, orderBy("className", "asc")), (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return { id: d.id, className: data.className || data.name || "", section: data.section || "" };
      });
      setClassesData(list);
    });

    // teachers
    const colTeachers = collection(db, "schools", school.id, "teachers");
    const unT = onSnapshot(query(colTeachers, orderBy("name", "asc")), (snap) => {
      const list = snap.docs.map(d => {
        const data = d.data() || {};
        return { id: d.id, name: data.name || "", email: data.email || "", primarySubject: data.primarySubject || "" };
      });
      setTeachersData(list);
    });

    // subjects (optional)
    const colSubjects = collection(db, "schools", school.id, "subjects");
    const unS = onSnapshot(query(colSubjects, orderBy("name", "asc")), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      setSubjectsData(list);
    });

    return () => { unC(); unT(); unS(); };
  }, [school?.id]);

  // filter options
  const classesList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.className && s.add(r.className));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const sectionsList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.section && s.add(r.section));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const branchesList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.branch && s.add(r.branch));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const subjectsList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.subject && s.add(r.subject));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    return rows.filter((r) => {
      if (cls !== "all" && r.className !== cls) return false;
      if (sec !== "all" && r.section !== sec) return false;
      if (branch !== "all" && r.branch !== branch) return false;  // NEW
      if (subj !== "all" && r.subject !== subj) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (q) {
        const hay = [r.title, r.subject, r.className, r.section, r.branch, r.description, r.attachURL, r.teacherName]
          .join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, qText, cls, sec, branch, subj, statusFilter]); // include branch in deps

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit = (row) => { setEditing(row); setShowModal(true); };

  // permissions
  const canEditOrDelete = (row) => {
    if (role === "schooladmin") return true;
    if (role === "teacher" && user?.uid) return row.createdBy === user.uid;
    return false;
  };

  // Create or update Firestore (single)
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const save = async (payload) => {
    if (!school?.id) return;
    const base = {
      // payload
      title: payload.title,
      subject: payload.subject,
      className: payload.className,
      section: payload.section,
      branch: payload.branch || "",                // NEW
      description: payload.description,
      dueAt: payload.dueAt || null,
      attachURL: payload.attachURL || "",
      maxMarks: payload.maxMarks ?? null,
      status: payload.status || "active",
      teacherId: payload.teacherId || "",
      teacherName: payload.teacherName || "",
      // metadata
      schoolId: school.id,
      updatedAt: serverTimestamp(),
    };

    if (editing) {
      if (!canEditOrDelete(editing)) {
        return Swal.fire({ icon: "info", title: "You don't have permission to update this assignment." });
      }
      await updateDoc(doc(db, "schools", school.id, "assignments", editing.id), base);
      Swal.fire({ icon: "success", title: "Updated", timer: 900, showConfirmButton: false });
    } else {
      await addDoc(collection(db, "schools", school.id, "assignments"), {
        ...base,
        createdBy: user?.uid || "",
        createdByRole: role || "",
        createdAt: serverTimestamp(),
      });
      Swal.fire({ icon: "success", title: "Created", timer: 900, showConfirmButton: false });
    }
  };

  const removeRow = async (row) => {
    if (!canEditOrDelete(row)) {
      return Swal.fire({ icon: "info", title: "You don't have permission to delete this assignment." });
    }
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete assignment?",
      text: `Remove "${row.title}" (${[row.className, row.section].filter(Boolean).join("-") || "No class"}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    await deleteDoc(doc(db, "schools", school.id, "assignments", row.id));
    Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
  };

  const exportCSV = () => {
    const data = filtered.map((r) => ({
      id: r.id,
      title: r.title,
      subject: r.subject,
      class: r.className,
      section: r.section,
      branch: r.branch || "",                         // NEW
      dueAt: fmt(r.dueAt, "YYYY-MM-DD HH:mm"),
      maxMarks: r.maxMarks ?? "",
      attachURL: r.attachURL,
      status: r.status,
      teacher: r.teacherName,
      createdByRole: r.createdByRole || "",
      createdAt: fmt(r.createdAt, "YYYY-MM-DD HH:mm"),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "assignments");
    XLSX.writeFile(wb, "assignments_export.csv");
  };

  if (loadingSchool || role == null) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <h5 className="mb-0">Assignments</h5>
          <small className="text-muted">
            {role === "teacher" ? "Your assignments" : `Create & manage assignments for ${school.name}`}
          </small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={exportCSV}>
              <i className="bi bi-download me-1" /> Export CSV
            </Button>
            <Button onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" /> Create Assignment
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <Card className="p-3">
        <Row className="g-2 align-items-end mb-2">
          <Col md={4}>
            <Form.Label className="mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                ref={searchRef}
                placeholder="Title, subject, class, section, branch, notes…"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
              {qText && (
                <Button variant="outline-secondary" onClick={() => setQText("")}>Clear</Button>
              )}
            </InputGroup>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Class</Form.Label>
            <Form.Select value={cls} onChange={(e) => setCls(e.target.value)}>
              {classesList.map((c) => <option key={c} value={c}>{c === "all" ? "All" : c}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Section</Form.Label>
            <Form.Select value={sec} onChange={(e) => setSec(e.target.value)}>
              {sectionsList.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Branch</Form.Label>
            <Form.Select value={branch} onChange={(e) => setBranch(e.target.value)}>
              {branchesList.map((b) => <option key={b} value={b}>{b === "all" ? "All" : b}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="mb-1">Subject</Form.Label>
            <Form.Select value={subj} onChange={(e) => setSubj(e.target.value)}>
              {subjectsList.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
            </Form.Select>
          </Col>
          <Col md={1}>
            <Form.Label className="mb-1">Status</Form.Label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Col>
          <Col md={1} className="text-end">
            <Badge bg="light" text="dark" className="border">
              {filtered.length} / {rows.length}
            </Badge>
          </Col>
        </Row>

        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead>
              <tr>
                <th>Title</th>
                <th className="d-none d-lg-table-cell">Subject</th>
                <th>Class</th>
                <th className="d-none d-md-table-cell">Due</th>
                <th className="d-none d-xl-table-cell">Max</th>
                <th className="d-none d-xl-table-cell">Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">No assignments found</td>
                </tr>
              ) : (
                filtered.map((a) => {
                  const clsDisp = [a.className, a.section, a.branch].filter(Boolean).join(" - ") || "—";
                  const overdue = a.dueAt ? dayjs(a.dueAt).isBefore(dayjs()) : false;
                  return (
                    <tr key={a.id}>
                      <td style={{ maxWidth: 320 }}>
                        <div className="fw-semibold text-truncate">{a.title}</div>
                        <div className="text-muted small text-truncate">
                          {a.attachURL || a.description}
                        </div>
                      </td>
                      <td className="d-none d-lg-table-cell">{a.subject || "—"}</td>
                      <td>{clsDisp}</td>
                      <td className="d-none d-md-table-cell">{fmt(a.dueAt)}</td>
                      <td className="d-none d-xl-table-cell">{a.maxMarks ?? "—"}</td>
                      <td className="d-none d-xl-table-cell">
                        {a.status === "inactive" ? (
                          <Badge bg="secondary">Inactive</Badge>
                        ) : overdue ? (
                          <Badge bg="dark">Due passed</Badge>
                        ) : (
                          <Badge bg="success">Active</Badge>
                        )}
                        {a.createdByRole && (
                          <span className="ms-2 small text-muted">by {a.createdByRole}</span>
                        )}
                      </td>
                      <td className="text-end">
                        {a.attachURL && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            title="Open attachment"
                            onClick={() => window.open(a.attachURL, "_blank")}
                          >
                            <i className="bi bi-box-arrow-up-right" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-2"
                          title="Edit"
                          onClick={() => { setEditing(a); setShowModal(true); }}
                          disabled={!canEditOrDelete(a)}
                        >
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          title="Delete"
                          onClick={() => removeRow(a)}
                          disabled={!canEditOrDelete(a)}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit modal */}
      <AssignmentFormModal
        show={showModal}
        onHide={() => { setShowModal(false); setEditing(null); }}
        initial={editing}
        onSave={async (payload) => {
          await save(payload);
          setShowModal(false);
          setEditing(null);
        }}
        classes={classesData}
        teachers={teachersData}
        subjects={subjectsData}
      />

      <style>{`
        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }
      `}</style>
    </Container>
  );
}
